import { useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { EditableQuestion } from '../../../../../types';
import { useInputFieldResetter } from '../components/StructuredInputEditor/hooks/useInputFieldResetter';

/**
 * Presence of `input` is the flag, so the switch is derived from the question rather than
 * persisted as a separate boolean. Held here because the switch sits beside the other
 * question settings while the builder it reveals needs the full width of the editor.
 *
 * Structured input only means anything on a free text question. React Hook Form keeps the
 * values of unmounted inputs, so a question retyped to something else would otherwise
 * still save the widget it no longer renders.
 */
export const useStructuredInput = (
  question: EditableQuestion,
  setValue: UseFormSetValue<EditableQuestion>,
  isOffered: boolean
) => {
  const [isSwitchedOn, setIsSwitchedOn] = useState(Boolean(question.input));

  // Derived as well as reset below, because the reset lands after the render in which
  // the question stopped being free text.
  const hasStructuredInput = isOffered && isSwitchedOn;

  useInputFieldResetter(hasStructuredInput, setValue);

  // Retyping the question clears `input`, so the switch has to come back off. Left on, it
  // would offer an emptied builder for a question that no longer has structured input.
  useEffect(() => {
    if (!isOffered) {
      setIsSwitchedOn(false);
    }
  }, [isOffered]);

  return { hasStructuredInput, setHasStructuredInput: setIsSwitchedOn };
};
